import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, User, List, CalendarDays, CalendarRange, Repeat, Pencil, Trash2, UserX, Users, CheckSquare, Square, Building2, MessageSquare, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Table, Btn, Badge, dt } from "../components/common";
import { Modal, Field, TextArea, Selecter } from "./crm/crmui";
import { useAuth } from "../auth";
import { useAutoRefresh } from "../lib/autoRefresh";

const ESTADO_TAREFA = ["por_fazer", "em_curso", "concluida"];
const MODOS = [
  { key: "lista", label: "Lista", icon: List },
  { key: "agenda", label: "Agenda", icon: CalendarDays },
  { key: "semana", label: "Semana", icon: CalendarRange },
];

const PRIO_CLS = {
  baixa: "bg-slate-100 text-slate-700 border-slate-200",
  media: "bg-sky-100 text-sky-700 border-sky-200",
  alta: "bg-amber-100 text-amber-800 border-amber-300",
  critica: "bg-red-100 text-red-800 border-red-300",
};
const ESTADO_CLS = {
  por_fazer: "border-l-slate-400",
  em_curso: "border-l-blue-500",
  concluida: "border-l-emerald-500 opacity-60",
};

// Retorna classes de cor do card baseado em estado + prazo (vermelho/amarelo/verde)
const taskCardCls = (r) => {
  const now = new Date().toISOString();
  if (r.estado === "concluida") return "bg-emerald-50/70 border-l-emerald-500 opacity-80";
  if (r.prazo && r.prazo < now) return "bg-red-50/80 border-l-red-500";
  return "bg-amber-50/50 border-l-amber-400";
};

function TasksSemana({ rows, members, onReschedule, onEstado, onManage }) {
  const [weekStart, setWeekStart] = useState(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); // 0=Dom
    const diff = day === 0 ? -6 : 1 - day; // Segunda = inicio
    d.setDate(d.getDate() + diff);
    return d;
  });
  const [dragged, setDragged] = useState(null);
  const [overDay, setOverDay] = useState(null);

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart); d.setDate(weekStart.getDate() + i);
      return d;
    });
  }, [weekStart]);

  const nameOf = (uid) => members.find((m) => m.id === uid)?.nome || "-";

  const byDay = useMemo(() => {
    const map = {};
    for (const d of days) map[d.toDateString()] = [];
    for (const r of (rows || [])) {
      if (!r.prazo) continue;
      const key = new Date(r.prazo).toDateString();
      if (map[key]) map[key].push(r);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => (a.prazo || "").localeCompare(b.prazo || ""));
    return map;
  }, [rows, days]);

  const semPrazo = useMemo(() => (rows || []).filter((r) => !r.prazo), [rows]);

  const prev = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); };
  const next = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); };
  const hoje = () => {
    const d = new Date(); d.setHours(0, 0, 0, 0);
    const day = d.getDay(); const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff); setWeekStart(d);
  };

  const handleDrop = async (e, day) => {
    e.preventDefault();
    setOverDay(null);
    if (!dragged) return;
    const oldDate = dragged.prazo ? new Date(dragged.prazo) : new Date();
    const newDate = new Date(day);
    newDate.setHours(oldDate.getHours() || 9, oldDate.getMinutes() || 0, 0, 0);
    await onReschedule(dragged.id, newDate.toISOString());
    setDragged(null);
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isToday = (d) => d.getTime() === today.getTime();

  return (
    <div data-testid="tasks-semana">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button onClick={prev} data-testid="semana-prev" className="px-2 py-1 border border-tint rounded hover:bg-tint text-sm">&laquo; Anterior</button>
          <button onClick={hoje} data-testid="semana-hoje" className="px-2 py-1 border border-tint rounded hover:bg-tint text-sm">Hoje</button>
          <button onClick={next} data-testid="semana-next" className="px-2 py-1 border border-tint rounded hover:bg-tint text-sm">Seguinte &raquo;</button>
        </div>
        <div className="text-sm text-slate-500">
          {days[0].toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })} - {days[6].toLocaleDateString("pt-PT", { day: "2-digit", month: "short", year: "numeric" })}
        </div>
      </div>
      <div className="grid grid-cols-7 gap-1.5" data-testid="semana-grid">
        {days.map((d) => {
          const key = d.toDateString();
          const items = byDay[key] || [];
          const active = overDay === key;
          return (
            <div key={key} data-testid={`semana-day-${d.toISOString().slice(0, 10)}`}
              onDragOver={(e) => { e.preventDefault(); setOverDay(key); }}
              onDragLeave={() => setOverDay((k) => k === key ? null : k)}
              onDrop={(e) => handleDrop(e, d)}
              className={`min-h-[260px] rounded-lg border ${active ? "border-blue-500 bg-blue-50" : "border-tint bg-white"} p-1.5 transition`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${isToday(d) ? "text-blue-700" : "text-slate-500"}`}>
                {d.toLocaleDateString("pt-PT", { weekday: "short" })} <span className="opacity-70">{d.getDate()}</span>
              </div>
              <div className="space-y-1">
                {items.map((r) => (
                  <div key={r.id} draggable onDragStart={() => setDragged(r)} onDragEnd={() => { setDragged(null); setOverDay(null); }}
                    data-testid={`semana-task-${r.id}`}
                    className={`border border-slate-200 border-l-4 ${taskCardCls(r)} rounded p-1.5 text-[11px] cursor-move hover:shadow`}>
                    <div className="font-semibold text-navy truncate">{r.titulo}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className={`inline-flex px-1 py-px rounded text-[9px] font-semibold ${PRIO_CLS[r.prioridade] || PRIO_CLS.media}`}>
                        {r.prioridade?.[0]?.toUpperCase() || "M"}
                      </span>
                      <span className="text-[9px] text-slate-500 truncate">
                        {new Date(r.prazo).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" })} · {nameOf(r.responsavel_id)}
                      </span>
                    </div>
                    {r.descricao && (
                      <div className="text-[9px] text-slate-400 mt-0.5 line-clamp-1 italic">{r.descricao.split("\n")[0]}</div>
                    )}
                    {(r.meta?.account_id || (r.ref_tipo === "account" && r.ref_id)) && (
                      <a href={`/app/crm/contas/${r.meta?.account_id || r.ref_id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-blue-600 text-[9px] hover:underline mt-0.5">
                        <Building2 size={9} /> {r.meta?.account_nome || "Conta"}
                      </a>
                    )}
                    <div className="flex items-center gap-1 mt-1">
                      <select value={r.estado} onChange={(e) => onEstado(r.id, e.target.value)}
                        data-testid={`semana-estado-${r.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 text-[10px] px-1 py-0.5 border border-tint rounded bg-white">
                        {ESTADO_TAREFA.map((s2) => <option key={s2} value={s2}>{s2.replace("_", " ")}</option>)}
                      </select>
                      {onManage && (
                        <button onClick={(e) => { e.stopPropagation(); onManage(r); }}
                          title="Gerir" className="p-0.5 rounded text-slate-400 hover:text-navy hover:bg-slate-100">
                          <Pencil size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && (
                  <div className="text-[10px] text-slate-300 italic text-center pt-4">Arraste tarefas aqui</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {semPrazo.length > 0 && (
        <div className="mt-4 border-t border-tint pt-3">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Sem prazo ({semPrazo.length}) - arraste para um dia</div>
          <div className="flex flex-wrap gap-1.5" data-testid="semana-sem-prazo">
            {semPrazo.map((r) => (
              <div key={r.id} draggable onDragStart={() => setDragged(r)} onDragEnd={() => setDragged(null)}
                data-testid={`semana-no-date-${r.id}`}
                className="bg-slate-100 border border-slate-300 rounded px-2 py-1 text-[11px] cursor-move hover:shadow">
                <span className="font-semibold text-navy">{r.titulo}</span>
                <span className="text-[10px] text-slate-500 ml-1">({nameOf(r.responsavel_id)})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TasksAgenda({ rows, members, onEstado, onManage }) {
  const nameOf = (uid) => members.find((m) => m.id === uid)?.nome || "-";
  const groups = useMemo(() => {
    const buckets = { atrasadas: [], hoje: [], amanha: [], semana: [], depois: [], sem_prazo: [] };
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const amanha = new Date(now); amanha.setDate(amanha.getDate() + 1);
    const daqui7 = new Date(now); daqui7.setDate(daqui7.getDate() + 7);
    for (const r of (rows || [])) {
      if (!r.prazo) { buckets.sem_prazo.push(r); continue; }
      const d = new Date(r.prazo);
      const dDay = new Date(d); dDay.setHours(0, 0, 0, 0);
      if (dDay < now) {
        // Passado: so mostra se nao concluida
        if (r.estado !== "concluida") buckets.atrasadas.push(r);
        // concluidas com prazo passado -> nao aparecem em nenhum bucket
      } else if (dDay.getTime() === now.getTime()) buckets.hoje.push(r);
      else if (dDay.getTime() === amanha.getTime()) buckets.amanha.push(r);
      else if (dDay <= daqui7) buckets.semana.push(r);
      else buckets.depois.push(r);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => (a.prazo || "").localeCompare(b.prazo || ""));
    }
    return buckets;
  }, [rows]);

  const sections = [
    { key: "atrasadas", label: "Atrasadas", cls: "text-red-700 bg-red-50 border-red-200" },
    { key: "hoje", label: "Hoje", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    { key: "amanha", label: "Amanha", cls: "text-blue-700 bg-blue-50 border-blue-200" },
    { key: "semana", label: "Proximos 7 dias", cls: "text-slate-700 bg-slate-50 border-slate-200" },
    { key: "depois", label: "Depois", cls: "text-slate-500 bg-slate-50 border-slate-200" },
    { key: "sem_prazo", label: "Sem prazo", cls: "text-slate-500 bg-slate-100 border-slate-200" },
  ];

  return (
    <div className="space-y-4" data-testid="tasks-agenda">
      {sections.map((s) => {
        const items = groups[s.key];
        if (!items || items.length === 0) return null;
        return (
          <div key={s.key} data-testid={`agenda-section-${s.key}`}>
            <div className={`flex items-center gap-2 text-xs font-bold uppercase tracking-wider border-l-4 pl-3 py-1 ${s.cls}`}>
              <CalendarDays size={12} /> {s.label}
              <span className="text-[10px] font-mono opacity-60">({items.length})</span>
            </div>
            <div className="mt-2 space-y-1.5">
              {items.map((r) => (
                <div key={r.id} data-testid={`agenda-item-${r.id}`}
                  className={`flex items-start gap-3 border border-tint border-l-4 ${taskCardCls(r)} rounded-md px-3 py-2 hover:shadow-sm transition`}>
                  <div className="min-w-[80px] text-[11px] text-slate-500 pt-0.5">
                    {r.prazo ? new Date(r.prazo).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" }) : "-"}
                    {r.prazo && (
                      <div className="text-[10px] text-slate-400">
                        {new Date(r.prazo).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-navy truncate">{r.titulo}</div>
                    <div className="text-[11px] text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
                      <span className={`inline-flex px-1.5 py-0.5 rounded border text-[10px] font-semibold ${PRIO_CLS[r.prioridade] || PRIO_CLS.media}`}>{r.prioridade}</span>
                      <span>{r.tipo}</span>
                      <span>·</span>
                      <span>{nameOf(r.responsavel_id) || "(automatica)"}</span>
                      {(r.meta?.account_id || (r.ref_tipo === "account" && r.ref_id)) && (
                        <a href={`/app/crm/contas/${r.meta?.account_id || r.ref_id}`}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`task-account-link-${r.id}`}
                          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-[11px]">
                          <Building2 size={11} /> {r.meta?.account_nome || "Ver conta"}
                        </a>
                      )}
                    </div>
                    {r.descricao && (
                      <div className="text-[10px] text-slate-500 mt-1 line-clamp-2 italic" data-testid={`task-desc-${r.id}`}>
                        {r.descricao.split("\n")[0]}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => onManage(r)} data-testid={`task-manage-btn-${r.id}`}
                      title="Gerir tarefa"
                      className="p-1 rounded text-slate-400 hover:text-navy hover:bg-slate-100 transition">
                      <Pencil size={13} />
                    </button>
                    <select value={r.estado} onChange={(e) => onEstado(r.id, e.target.value)}
                      data-testid={`agenda-estado-${r.id}`}
                      className="px-2 py-1 border border-tint rounded text-xs bg-white">
                      {ESTADO_TAREFA.map((s2) => <option key={s2} value={s2}>{s2.replace("_", " ")}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {Object.values(groups).every((v) => v.length === 0) && (
        <div className="text-center py-10 text-slate-400 text-sm italic">Sem tarefas no periodo.</div>
      )}
    </div>
  );
}

export default function Tasks() {
  const [params, setParams] = useSearchParams();
  const { user, can } = useAuth();
  const isSuperAdmin = can("*");
  const initialVista = (params.get("vista") === "minhas" || localStorage.getItem("tarefas.vista") === "minhas") ? "minhas" : "todas";
  const initialModo = ((v) => (v === "agenda" || v === "semana") ? v : "lista")(params.get("modo") || localStorage.getItem("tarefas.modo"));
  const [vista, setVista] = useState(initialVista);
  const [modo, setModo] = useState(initialModo);
  const [rows, setRows] = useState([]);
  const [members, setMembers] = useState([]);
  const [filtro, setFiltro] = useState("todas");
  const [filtroResp, setFiltroResp] = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [show, setShow] = useState(false);
  // Bulk assign (modo sem_responsavel)
  const [selected, setSelected] = useState(new Set());
  const [bulkResp, setBulkResp] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [seriesFor, setSeriesFor] = useState(null);
  const [seriesData, setSeriesData] = useState(null);
  const defaultTask = { titulo: "", descricao: "", tipo: "tarefa", responsavel_id: "", prioridade: "media", prazo: "", ref_tipo: "", ref_id: "",
    recorrencia_tipo: "", recorrencia_intervalo: 1, recorrencia_dias: [], recorrencia_dia_mes: "", recorrencia_fim: "" };
  const [task, setTask] = useState(defaultTask);

  const toLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openNew = () => { setEditingId(null); setTask(defaultTask); setShow(true); };

  const openEdit = (r) => {
    setEditingId(r.id);
    setTask({
      titulo: r.titulo || "", descricao: r.descricao || "", tipo: r.tipo || "tarefa",
      responsavel_id: r.responsavel_id || "", prioridade: r.prioridade || "media",
      prazo: toLocalInput(r.prazo), ref_tipo: r.ref_tipo || "", ref_id: r.ref_id || "",
      recorrencia_tipo: r.recorrencia?.tipo || "", recorrencia_intervalo: r.recorrencia?.intervalo || 1,
      recorrencia_dias: r.recorrencia?.dias_semana || [], recorrencia_dia_mes: r.recorrencia?.dia_mes || "",
      recorrencia_fim: r.recorrencia?.fim_em || "",
    });
    setShow(true);
  };

  const closeModal = () => { setShow(false); setEditingId(null); setTask(defaultTask); };

  const removeTask = async (r) => {
    if (!window.confirm(`Eliminar definitivamente a tarefa "${r.titulo}"? Esta accao nao pode ser revertida.`)) return;
    try {
      await api.delete(`/team/tasks/${r.id}`);
      toast.success("Tarefa eliminada.");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!seriesFor) { setSeriesData(null); return; }
    api.get(`/team/tasks/${seriesFor.id}/series`).then((r) => setSeriesData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [seriesFor]);

  const load = async () => {
    try {
      const params = filtro === "atrasadas" ? { atrasadas: true } : (filtro !== "todas" ? { estado: filtro } : {});
      const effectiveResp = vista === "minhas" ? (user?.id || "") : filtroResp;
      if (effectiveResp === "__sem_responsavel__") {
        params.sem_responsavel = true;
      } else if (effectiveResp) {
        params.responsavel_id = effectiveResp;
      }
      const [t, m] = await Promise.all([api.get("/team/tasks", { params }), api.get("/team/members")]);
      let rows = t.data;
      if (filtroTipo) rows = rows.filter((r) => r.tipo === filtroTipo);
      setRows(rows); setMembers(m.data);
      setSelected(new Set()); // limpar selecao ao recarregar
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [filtro, filtroResp, filtroTipo, vista, user?.id]);
  useAutoRefresh(load); // eslint-disable-line

  // Persistir vista + sincronizar querystring
  useEffect(() => {
    localStorage.setItem("tarefas.vista", vista);
    const next = new URLSearchParams(params);
    if (vista === "minhas") next.set("vista", "minhas"); else next.delete("vista");
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
    if (vista === "minhas" && filtroResp) setFiltroResp("");
  }, [vista]); // eslint-disable-line

  useEffect(() => {
    localStorage.setItem("tarefas.modo", modo);
    const next = new URLSearchParams(params);
    if (modo === "lista") next.delete("modo"); else next.set("modo", modo);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [modo]); // eslint-disable-line

  // Suporta abertura directa a partir de /app/crm/contas: ?nova=1&account_id=&titulo=
  useEffect(() => {
    if (params.get("nova") === "1") {
      setTask({
        titulo: params.get("titulo") || "",
        descricao: params.get("descricao") || "",
        tipo: "tarefa", responsavel_id: "", prioridade: "media", prazo: "",
        ref_tipo: params.get("account_id") ? "account" : "",
        ref_id: params.get("account_id") || "",
      });
      setShow(true);
      const next = new URLSearchParams(params);
      ["nova", "account_id", "titulo", "descricao"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const saveTask = async () => {
    if (!task.titulo.trim()) return toast.error("Indique o titulo.");
    try {
      const body = { titulo: task.titulo, descricao: task.descricao, tipo: task.tipo,
        responsavel_id: task.responsavel_id, prioridade: task.prioridade, prazo: task.prazo,
        ref_tipo: task.ref_tipo, ref_id: task.ref_id };
      if (body.prazo) body.prazo = new Date(body.prazo).toISOString();
      if (task.recorrencia_tipo) {
        body.recorrencia = {
          tipo: task.recorrencia_tipo,
          intervalo: parseInt(task.recorrencia_intervalo) || 1,
          dias_semana: task.recorrencia_tipo === "weekly" ? (task.recorrencia_dias || []).map((d) => parseInt(d)) : [],
          dia_mes: task.recorrencia_tipo === "monthly" && task.recorrencia_dia_mes ? parseInt(task.recorrencia_dia_mes) : null,
          fim_em: task.recorrencia_fim || "",
        };
      }
      if (editingId) {
        await api.put(`/team/tasks/${editingId}`, body);
        toast.success("Tarefa atualizada.");
      } else {
        await api.post("/team/tasks", body);
        toast.success("Tarefa criada.");
      }
      closeModal();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setEstado = async (id, estado) => {
    try { await api.put(`/team/tasks/${id}`, { estado }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reschedule = async (id, prazo) => {
    try { await api.put(`/team/tasks/${id}`, { prazo }); toast.success("Tarefa reagendada."); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isSemResp = filtroResp === "__sem_responsavel__";

  // Estado do modal de gestao (comentario + adiamento)
  const [manageTask, setManageTask] = useState(null); // tarefa seleccionada
  const [manageForm, setManageForm] = useState({ comentario: "", prazo: "", motivo_adiamento: "" });
  const [manageBusy, setManageBusy] = useState(false);

  const openManage = (r) => {
    setManageTask(r);
    setManageForm({ comentario: "", prazo: r.prazo ? r.prazo.slice(0, 16) : "", motivo_adiamento: "" });
  };

  const saveManage = async () => {
    if (!manageTask) return;
    setManageBusy(true);
    try {
      const upd = {};
      // Adiamento
      if (manageForm.prazo && manageForm.prazo !== (manageTask.prazo || "").slice(0, 16)) {
        upd.prazo = new Date(manageForm.prazo).toISOString();
      }
      // Comentario
      if (manageForm.comentario.trim()) {
        const now = new Date().toLocaleString("pt-PT");
        const motivo = manageForm.motivo_adiamento ? ` [${manageForm.motivo_adiamento}]` : "";
        const entrada = `[${now}${motivo}] ${manageForm.comentario.trim()}`;
        upd.descricao = manageTask.descricao ? `${manageTask.descricao}\n${entrada}` : entrada;
      }
      if (Object.keys(upd).length === 0) { toast.info("Sem alteracoes para guardar."); return; }
      await api.put(`/team/tasks/${manageTask.id}`, upd);
      toast.success("Tarefa actualizada.");
      setManageTask(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao actualizar tarefa.");
    } finally { setManageBusy(false); }
  };

  // Cor e ordem das linhas na vista lista
  const _now = new Date().toISOString();
  const _taskPriority = (r) => {
    if (r.estado === "concluida") return 3;
    if (r.prazo && r.prazo < _now) return 1; // atrasada
    return 2; // por fazer / em curso
  };
  const taskRowClass = (r) => {
    if (r.estado === "concluida") return "bg-emerald-50/70 hover:bg-emerald-100/60";
    if (r.prazo && r.prazo < _now) return "bg-red-50/70 hover:bg-red-100/50";
    return "bg-amber-50/50 hover:bg-amber-100/40";
  };
  const sortedRows = useMemo(() => {
    const now = new Date().toISOString();
    const pri = (r) => {
      if (r.estado === "concluida") return 3;
      if (r.prazo && r.prazo < now) return 1;
      return 2;
    };
    return [...rows].sort((a, b) => pri(a) - pri(b) || (a.prazo || "").localeCompare(b.prazo || ""));
  }, [rows]);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)));
  };

  const bulkAssign = async () => {
    if (!bulkResp) return toast.error("Selecione um responsavel.");
    if (selected.size === 0) return toast.error("Selecione pelo menos uma tarefa.");
    setBulkLoading(true);
    try {
      const r = await api.post("/team/tasks/bulk-assign", { task_ids: [...selected], responsavel_id: bulkResp });
      toast.success(`${r.data.atualizadas} tarefa(s) atribuida(s).`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBulkLoading(false); }
  };

  const nameOf = (uid) => members.find((m) => m.id === uid)?.nome || "-";

  const baseCols = [
    { key: "titulo", label: "Titulo", render: (r) => (
      <span className="font-semibold text-navy flex items-center gap-1.5">
        {r.titulo}
        {r.recorrencia?.tipo && (
          <button onClick={() => setSeriesFor(r)} data-testid={`task-recurring-${r.id}`}
            title={`Ver serie (${r.recorrencia.tipo === "weekly" ? "semanalmente" : r.recorrencia.tipo === "monthly" ? "mensalmente" : "anualmente"})`}
            className="inline-flex items-center gap-0.5 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 px-1.5 py-0.5 rounded transition">
            <Repeat size={9} /> {r.recorrencia.tipo === "weekly" ? "SEM" : r.recorrencia.tipo === "monthly" ? "MES" : "ANO"}
          </button>
        )}
      </span>
    ) },
    { key: "conta", label: "Conta", render: (r) => {
      const accId = r.meta?.account_id || (r.ref_tipo === "account" ? r.ref_id : "");
      const accNome = r.meta?.account_nome || "";
      if (!accId) return <span className="text-slate-400 text-xs">-</span>;
      return (
        <a href={`/app/crm/contas/${accId}`} data-testid={`task-list-account-${r.id}`}
          className="inline-flex items-center gap-1 text-blue-600 hover:underline text-xs font-medium">
          <Building2 size={12} /> {accNome || "Ver conta"}
        </a>
      );
    } },
    { key: "tipo", label: "Tipo" },
    { key: "prioridade", label: "Prioridade", render: (r) => <Badge value={r.prioridade} /> },
    { key: "responsavel_id", label: "Responsavel", render: (r) => r.criador_id === "workflow" && !r.responsavel_id ? "(automatica)" : nameOf(r.responsavel_id) },
    { key: "prazo", label: "Prazo", render: (r) => r.prazo ? dt(r.prazo) : "-" },
    { key: "estado", label: "Estado", render: (r) => (
      <select value={r.estado} onChange={(e) => setEstado(r.id, e.target.value)} data-testid={`task-estado-${r.id}`} className="px-2 py-1 border border-tint rounded text-xs bg-white">
        {ESTADO_TAREFA.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
      </select>
    ) },
    { key: "gerir", label: "", render: (r) => (
      <button onClick={() => openManage(r)} data-testid={`task-list-manage-${r.id}`}
        title="Gerir / adiar"
        className="p-1 rounded text-slate-400 hover:text-navy hover:bg-slate-100 transition">
        <Pencil size={13} />
      </button>
    ) },
  ];
  if (isSuperAdmin) {
    baseCols.push({ key: "acoes", label: "Acoes", render: (r) => (
      <div className="flex items-center gap-3">
        <button onClick={() => openEdit(r)} data-testid={`task-edit-${r.id}`}
          className="inline-flex items-center gap-1 text-blue text-xs font-semibold hover:underline"><Pencil size={12} /> Editar</button>
        <button onClick={() => removeTask(r)} data-testid={`task-delete-${r.id}`}
          className="inline-flex items-center gap-1 text-red-600 text-xs font-semibold hover:underline"><Trash2 size={12} /> Eliminar</button>
      </div>
    ) });
  }

  // No modo "sem responsavel": coluna de selecao + botao atribuir por linha
  const cols = isSemResp ? [
    { key: "sel", label: (
      <button onClick={toggleAll} className="p-0.5" data-testid="bulk-select-all">
        {selected.size === rows.length && rows.length > 0 ? <CheckSquare size={14} className="text-blue" /> : <Square size={14} className="text-slate-400" />}
      </button>
    ), render: (r) => (
      <button onClick={() => toggleSelect(r.id)} className="p-0.5" data-testid={`bulk-sel-${r.id}`}>
        {selected.has(r.id) ? <CheckSquare size={14} className="text-blue" /> : <Square size={14} className="text-slate-400" />}
      </button>
    ) },
    ...baseCols,
  ] : baseCols;

  return (
    <>
      <PageHeader overline="Equipa" title="Tarefas" action={
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-tint overflow-hidden" data-testid="modo-toggle">
            {MODOS.map((m) => {
              const Icon = m.icon;
              return (
                <button key={m.key} onClick={() => setModo(m.key)} data-testid={`modo-${m.key}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold transition ${modo === m.key ? "bg-navy text-white" : "bg-white text-navy hover:bg-tint"}`}>
                  <Icon size={13} /> {m.label}
                </button>
              );
            })}
          </div>
          <Btn onClick={openNew} testid="new-task-btn"><Plus size={16} /> Nova tarefa</Btn>
        </div>
      } />
      <div className="flex flex-wrap gap-2 mb-3 items-center" data-testid="task-vista">
        <button onClick={() => { setVista("todas"); setFiltroResp(""); }} data-testid="vista-todas"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${vista === "todas" && filtroResp !== "__sem_responsavel__" ? "bg-navy text-white" : "bg-white border border-tint text-navy"}`}>Todas</button>
        <button onClick={() => { setVista("minhas"); setFiltroResp(""); }} data-testid="vista-minhas"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${vista === "minhas" ? "bg-navy text-white" : "bg-white border border-tint text-navy"}`}>
          <User size={14} /> Minhas tarefas
        </button>
        <button onClick={() => { setVista("todas"); setFiltroResp("__sem_responsavel__"); }} data-testid="vista-sem-responsavel"
          className={`px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${filtroResp === "__sem_responsavel__" ? "bg-amber-600 text-white" : "bg-white border border-amber-200 text-amber-700 hover:bg-amber-50"}`}>
          <UserX size={14} /> Sem responsavel
          {filtroResp === "__sem_responsavel__" && rows.length > 0 && (
            <span className="bg-white/30 text-white text-[10px] font-bold px-1.5 rounded-full">{rows.length}</span>
          )}
        </button>
        {vista === "minhas" && user?.nome && (
          <span className="text-xs text-slate-500">a filtrar por <b>{user.nome}</b></span>
        )}
      </div>

      {/* Barra de atribuicao em massa - visivel apenas no modo sem responsavel */}
      {isSemResp && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="bulk-assign-bar">
          <Users size={15} className="text-amber-700 shrink-0" />
          <span className="text-xs text-amber-800 font-semibold">{selected.size > 0 ? `${selected.size} selecionada(s)` : "Selecione tarefas para atribuir"}</span>
          <div className="flex items-center gap-2 ml-auto">
            <select value={bulkResp} onChange={(e) => setBulkResp(e.target.value)}
              data-testid="bulk-resp-select"
              className="text-sm border border-amber-300 bg-white rounded px-2 py-1 text-navy">
              <option value="">Atribuir a...</option>
              {members.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
            </select>
            <Btn onClick={bulkAssign} disabled={bulkLoading || selected.size === 0 || !bulkResp}
              testid="bulk-assign-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1.5">
              {bulkLoading ? "A atribuir..." : `Atribuir ${selected.size > 0 ? `(${selected.size})` : ""}`}
            </Btn>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="task-filters">
        {["todas", "por_fazer", "em_curso", "concluida", "atrasadas"].map((f) => (
          <button key={f} onClick={() => setFiltro(f)} data-testid={`filter-${f}`}
            className={`px-3 py-1.5 rounded-lg text-sm ${filtro === f ? "bg-blue text-white" : "bg-tint text-navy"}`}>{f.replace("_", " ")}</button>
        ))}
        <div className="w-px h-6 bg-tint mx-1" />
        <select value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}
          data-testid="filter-tipo"
          className="text-sm border border-tint bg-white rounded-lg px-2 py-1.5">
          <option value="">Todos os tipos</option>
          <option value="tarefa">Tarefa</option>
          <option value="chamada">Chamada</option>
          <option value="reuniao">Reuniao</option>
          <option value="follow-up">Follow-up</option>
          <option value="documento">Documento</option>
          <option value="verificacao">Verificacao</option>
        </select>
        <select value={filtroResp} onChange={(e) => setFiltroResp(e.target.value)}
          data-testid="filter-responsavel"
          disabled={vista === "minhas"}
          className="text-sm border border-tint bg-white rounded-lg px-2 py-1.5 max-w-[240px] disabled:opacity-50 disabled:cursor-not-allowed">
          <option value="">Todos os responsaveis</option>
          {members.map((m) => <option key={m.id} value={m.id}>{m.nome}</option>)}
        </select>
        {(filtroResp || filtroTipo) && (
          <button onClick={() => { setFiltroResp(""); setFiltroTipo(""); }}
            data-testid="filter-clear"
            className="text-[11px] font-semibold text-slate-500 hover:text-red-600 px-2">Limpar filtros</button>
        )}
      </div>
      {modo === "lista" ? (
        <Table testid="tasks-table" columns={cols} rows={sortedRows} rowClass={taskRowClass} />
      ) : modo === "semana" ? (
        <TasksSemana rows={rows} members={members} onReschedule={reschedule} onEstado={setEstado} />
      ) : (
        <TasksAgenda rows={rows} members={members} onEstado={setEstado} onManage={openManage} />
      )}

      <Modal open={show} onClose={closeModal} title={editingId ? "Editar tarefa" : "Nova tarefa"} testid="task-modal">
        <Field label="Titulo" value={task.titulo} onChange={(v) => setTask({ ...task, titulo: v })} testid="task-titulo" />
        <TextArea label="Descricao" value={task.descricao} onChange={(v) => setTask({ ...task, descricao: v })} testid="task-descricao" />
        <Selecter label="Tipo" value={task.tipo} onChange={(v) => setTask({ ...task, tipo: v })} options={["tarefa", "chamada", "reuniao", "follow-up", "documento", "verificacao"]} testid="task-tipo" />
        <Selecter label="Prioridade" value={task.prioridade} onChange={(v) => setTask({ ...task, prioridade: v })} options={["baixa", "media", "alta"]} testid="task-prioridade" />
        <Selecter label="Responsavel" value={task.responsavel_id} onChange={(v) => setTask({ ...task, responsavel_id: v })} options={[{ value: "", label: "- Nenhum -" }, ...members.map((m) => ({ value: m.id, label: m.nome }))]} testid="task-responsavel" />
        <Field label="Prazo" type="datetime-local" value={task.prazo} onChange={(v) => setTask({ ...task, prazo: v })} testid="task-prazo" />
        <div className="border-t border-tint pt-3 mt-2" data-testid="task-recorrencia-section">
          <Selecter label="Repetir" value={task.recorrencia_tipo} onChange={(v) => setTask({ ...task, recorrencia_tipo: v })}
            options={[
              { value: "", label: "Nao repetir (unica)" },
              { value: "weekly", label: "Semanalmente" },
              { value: "monthly", label: "Mensalmente" },
              { value: "yearly", label: "Anualmente" },
            ]} testid="task-recorrencia-tipo" />
          {task.recorrencia_tipo && (
            <>
              <Field label="A cada N periodos" type="number" value={task.recorrencia_intervalo}
                onChange={(v) => setTask({ ...task, recorrencia_intervalo: v })} testid="task-recorrencia-intervalo" />
              {task.recorrencia_tipo === "weekly" && (
                <div className="mt-2" data-testid="task-recorrencia-dias-semana">
                  <label className="block text-xs font-semibold text-slate-600 mb-1">Dias da semana</label>
                  <div className="flex flex-wrap gap-1">
                    {[["Seg", 0], ["Ter", 1], ["Qua", 2], ["Qui", 3], ["Sex", 4], ["Sab", 5], ["Dom", 6]].map(([label, val]) => {
                      const active = (task.recorrencia_dias || []).includes(val);
                      return (
                        <button key={val} type="button" data-testid={`task-recorrencia-dia-${val}`}
                          onClick={() => {
                            const next = active
                              ? task.recorrencia_dias.filter((d) => d !== val)
                              : [...(task.recorrencia_dias || []), val];
                            setTask({ ...task, recorrencia_dias: next });
                          }}
                          className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition ${active ? "bg-navy text-white border-navy" : "bg-white text-navy border-tint"}`}>
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {task.recorrencia_tipo === "monthly" && (
                <Field label="Dia do mes (opcional)" type="number" value={task.recorrencia_dia_mes}
                  onChange={(v) => setTask({ ...task, recorrencia_dia_mes: v })} testid="task-recorrencia-dia-mes" />
              )}
              <Field label="Termina em (opcional)" type="date" value={task.recorrencia_fim}
                onChange={(v) => setTask({ ...task, recorrencia_fim: v })} testid="task-recorrencia-fim" />
              <p className="text-[11px] text-slate-500 mt-1">
                Ao concluir a tarefa, cria-se automaticamente a proxima instancia com o novo prazo.
              </p>
            </>
          )}
        </div>
        <div className="flex justify-end gap-2 pt-2"><Btn variant="ghost" onClick={closeModal}>Cancelar</Btn><Btn onClick={saveTask} testid="task-save">{editingId ? "Guardar" : "Criar"}</Btn></div>
      </Modal>

      {/* Modal de gestao / adiamento de tarefa */}
      <Modal open={!!manageTask} onClose={() => setManageTask(null)} title="Gerir tarefa" testid="task-manage-modal">
        {manageTask && (
          <div className="space-y-3">
            <div className="bg-slate-50 border border-tint rounded-md p-3">
              <div className="font-semibold text-navy">{manageTask.titulo}</div>
              {manageTask.meta?.account_nome && (
                <div className="text-[11px] text-blue-600 flex items-center gap-1 mt-1"><Building2 size={11} /> {manageTask.meta.account_nome}</div>
              )}
              {manageTask.descricao && (
                <div className="text-[11px] text-slate-500 mt-1 whitespace-pre-wrap max-h-24 overflow-y-auto italic">{manageTask.descricao}</div>
              )}
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Reagendar para</label>
              <input type="datetime-local" value={manageForm.prazo} onChange={(e) => setManageForm({ ...manageForm, prazo: e.target.value })}
                data-testid="manage-prazo" className="w-full border border-tint rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Motivo do adiamento</label>
              <select value={manageForm.motivo_adiamento} onChange={(e) => setManageForm({ ...manageForm, motivo_adiamento: e.target.value })}
                data-testid="manage-motivo" className="w-full border border-tint rounded px-3 py-2 text-sm bg-white">
                <option value="">- Seleccionar motivo -</option>
                <option value="Pessoa nao estava presente">Pessoa nao estava presente</option>
                <option value="Numero errado / indisponivel">Numero errado / indisponivel</option>
                <option value="Pediu para ligar mais tarde">Pediu para ligar mais tarde</option>
                <option value="Reagendado a pedido do cliente">Reagendado a pedido do cliente</option>
                <option value="Caixa de voz">Caixa de voz</option>
                <option value="Sem resposta">Sem resposta</option>
                <option value="Outro">Outro</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase font-semibold text-slate-500 mb-1">Comentario</label>
              <textarea rows={3} value={manageForm.comentario} onChange={(e) => setManageForm({ ...manageForm, comentario: e.target.value })}
                data-testid="manage-comentario" placeholder="Adicionar comentario ou nota..."
                className="w-full border border-tint rounded px-3 py-2 text-sm" />
              <p className="text-[10px] text-slate-400 mt-0.5">Adicionado ao historico com data/hora e motivo.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="ghost" onClick={() => setManageTask(null)}>Cancelar</Btn>
              <Btn onClick={saveManage} disabled={manageBusy} testid="manage-save-btn">{manageBusy ? "A guardar..." : "Guardar"}</Btn>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!seriesFor} onClose={() => setSeriesFor(null)} title={`Serie: ${seriesFor?.titulo || ""}`} testid="task-series-modal">
        {!seriesData ? <p className="text-sm text-muted2">A carregar...</p> : (
          <div className="space-y-3" data-testid="task-series-content">
            <div className="text-xs text-slate-500 bg-slate-50 rounded p-2">
              <b>Recorre:</b> {seriesData.recorrencia?.tipo === "weekly" ? "semanalmente" : seriesData.recorrencia?.tipo === "monthly" ? "mensalmente" : "anualmente"}
              {seriesData.recorrencia?.intervalo > 1 && <span> (a cada {seriesData.recorrencia.intervalo})</span>}
              {seriesData.recorrencia?.dias_semana?.length > 0 && (
                <span> - dias: {seriesData.recorrencia.dias_semana.map((d) => ["Seg","Ter","Qua","Qui","Sex","Sab","Dom"][d]).join(", ")}</span>
              )}
              {seriesData.recorrencia?.fim_em && <span> - termina em {seriesData.recorrencia.fim_em}</span>}
              <span className="block mt-1">Total de instancias: <b>{seriesData.items?.length || 0}</b></span>
            </div>
            <div className="max-h-96 overflow-y-auto space-y-1.5">
              {(seriesData.items || []).map((t, i) => (
                <div key={t.id} data-testid={`series-item-${i}`}
                  className={`flex items-center gap-3 border border-tint border-l-4 rounded px-3 py-2 ${ESTADO_CLS[t.estado] || ""}`}>
                  <div className="min-w-[110px] text-[11px] text-slate-500">
                    {t.prazo ? new Date(t.prazo).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Sem prazo"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-navy truncate">{t.titulo}</div>
                    <div className="text-[10px] text-slate-500">
                      <span className={`inline-flex px-1.5 py-0.5 rounded border ${PRIO_CLS[t.prioridade] || PRIO_CLS.media} mr-1`}>{t.prioridade}</span>
                      {t.parent_task_id && <span className="text-emerald-700">gerada por conclusao</span>}
                      {!t.parent_task_id && <span className="text-slate-500">origem</span>}
                    </div>
                  </div>
                  <Badge value={t.estado} />
                </div>
              ))}
            </div>
            <div className="flex justify-end pt-2">
              <Btn variant="ghost" onClick={() => setSeriesFor(null)}>Fechar</Btn>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
